import React, { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import SubmitButton from '../formComponents/SubmitButton';
import ErrorMessage from '../formComponents/ErrorMessage';
import SelectDropdown from '../formComponents/SelectDropdown';
import SearchableSelectDropdown from '../formComponents/SearchableSelectDropdown';
import {
  TEAM_METADATA_CATEGORIES,
  TEAM_METADATA_TYPES_BY_CATEGORY,
  TEAM_METADATA_TYPE_TO_CATEGORIES,
  TEAM_METADATA_TYPE_TO_CATEGORY,
  normalizeTeamMetadata,
} from '../../teams/utils/teamMetadata';

const ageGroups = ['Children', 'Teens', 'Adults', 'Mixed'];

const justificationOptions = [
  'Legal compliance (e.g. safeguarding)',
  'Activity is unsuitable for certain ages',
  'Venue restrictions',
  'Supervision/resource limitations',
  'Group decision or tradition',
  'Other',
];

function sortByReference(values = [], reference = []) {
  const allowed = new Set(values);
  return reference.filter((item) => allowed.has(item));
}

export default function EditMetadataForm({ initialMetadata, initialName = '', initialDescription = '', isOpen, onClose, onSave }) {
  const metadata = useMemo(() => initialMetadata ?? {}, [initialMetadata]);
  const normalizedMetadata = useMemo(() => normalizeTeamMetadata(metadata), [metadata]);
  const [name, setName] = useState(initialName || '');
  const [description, setDescription] = useState(initialDescription || '');
  const [categories, setCategories] = useState(normalizedMetadata.categories);
  const [types, setTypes] = useState(normalizedMetadata.types);
  const [primaryLabel, setPrimaryLabel] = useState(normalizedMetadata.primaryLabel);
  const [ageGroup, setAgeGroup] = useState(metadata.ageGroup || '');
  const [ageMin, setAgeMin] = useState(metadata.ageRestrictions?.min ?? '');
  const [ageMax, setAgeMax] = useState(metadata.ageRestrictions?.max ?? '');
  const [justification, setJustification] = useState(metadata.justification || '');

  const [error, setError] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const availableTypeCategories = useMemo(
    () => categories.filter((category) => Array.isArray(TEAM_METADATA_TYPES_BY_CATEGORY[category])),
    [categories]
  );
  const availableTypes = useMemo(
    () => Array.from(new Set(
      availableTypeCategories.flatMap((category) => TEAM_METADATA_TYPES_BY_CATEGORY[category] || [])
    )),
    [availableTypeCategories]
  );
  const primaryOptions = useMemo(() => [...categories, ...types], [categories, types]);

  useEffect(() => {
    setName(initialName || '');
    setDescription(initialDescription || '');
    setCategories(normalizedMetadata.categories);
    setTypes(normalizedMetadata.types);
    setPrimaryLabel(normalizedMetadata.primaryLabel);
    setAgeGroup(metadata.ageGroup || '');
    setAgeMin(metadata.ageRestrictions?.min ?? '');
    setAgeMax(metadata.ageRestrictions?.max ?? '');
    setJustification(metadata.justification || '');
  }, [initialDescription, initialName, metadata, normalizedMetadata]);

  useEffect(() => {
    if (primaryOptions.includes(primaryLabel)) return;
    setPrimaryLabel(primaryOptions[0] || '');
  }, [primaryLabel, primaryOptions]);

  const handleCategoriesChange = (nextCategories) => {
    const sortedCategories = sortByReference(nextCategories, TEAM_METADATA_CATEGORIES);
    const allowedCategories = new Set(sortedCategories);
    setCategories(sortedCategories);
    setTypes((prev) => prev.filter((type) =>
      (TEAM_METADATA_TYPE_TO_CATEGORIES[type] || []).some((category) => allowedCategories.has(category))
    ));
  };

  const handleTypesChange = (nextTypes) => {
    const sortedTypes = sortByReference(nextTypes, availableTypes);
    const parentCategories = sortedTypes.flatMap((type) => {
      const typeCategories = TEAM_METADATA_TYPE_TO_CATEGORIES[type] || [];
      return typeCategories.some((category) => categories.includes(category))
        ? []
        : typeCategories.slice(0, 1);
    });
    setCategories((prev) => sortByReference([...prev, ...parentCategories], TEAM_METADATA_CATEGORIES));
    setTypes(sortedTypes);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError(null);

    try {
      const trimmedName = name.trim();
      if (!trimmedName) {
        throw new Error('Club name is required.');
      }
      const finalTypes = sortByReference(types, availableTypes);
      const requiredParentCategories = finalTypes.flatMap((type) => {
        const typeCategories = TEAM_METADATA_TYPE_TO_CATEGORIES[type] || [];
        return typeCategories.some((category) => categories.includes(category))
          ? []
          : typeCategories.slice(0, 1);
      });
      const finalCategories = sortByReference(
        Array.from(new Set([
          ...categories,
          ...requiredParentCategories,
        ])),
        TEAM_METADATA_CATEGORIES
      );
      const finalPrimaryType = finalTypes.includes(primaryLabel) ? primaryLabel : '';
      const finalPrimaryCategory = finalCategories.includes(primaryLabel)
        ? primaryLabel
        : (finalPrimaryType
          ? (TEAM_METADATA_TYPE_TO_CATEGORIES[finalPrimaryType] || [])
            .find((category) => finalCategories.includes(category))
            || TEAM_METADATA_TYPE_TO_CATEGORY[finalPrimaryType]
            || finalCategories[0]
            || ''
          : finalCategories[0] || '');
      const payload = {
        sector: finalPrimaryType || finalPrimaryCategory || '',
        categories: finalCategories,
        types: finalTypes,
        primaryCategory: finalPrimaryCategory,
        primaryType: finalPrimaryType,
        ageGroup,
      };

      if (ageMin || ageMax) {
        payload.ageRestrictions = {
          ...(ageMin && { min: Number(ageMin) }),
          ...(ageMax && { max: Number(ageMax) }),
        };
        if (!justification) {
          throw new Error('Justification is required when setting age restrictions.');
        }
        payload.justification = justification;
      }

      await onSave({
        name: trimmedName,
        description: description.trim(),
        teamMetadata: payload,
      });
      onClose();
    } catch (err) {
      setError(err.message || 'Failed to update team details');
    } finally {
      setSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <>
        <h2 className="modal-title">Edit Club Details</h2>
        {error && <ErrorMessage message={error} />}

        <form onSubmit={handleSubmit} className="modal-form">
          <div className="form-group">
            <label className="form-label" htmlFor="clubName">Club Name</label>
            <input
              id="clubName"
              className="form-input w-full"
              value={name}
              onChange={(e) => setName(e.target.value)}
              required
              disabled={submitting}
            />
          </div>

          <div className="form-group">
            <label className="form-label" htmlFor="clubDescription">Public Description</label>
            <textarea
              id="clubDescription"
              className="form-input w-full"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={3}
              placeholder="A short public description shown on club pages and search results."
              disabled={submitting}
            />
            <p className="text-muted" style={{ marginTop: '0.35rem', fontSize: '0.9rem' }}>
              This appears on public-facing club surfaces such as search results, join pages, and the club dashboard header.
            </p>
          </div>

          <SearchableSelectDropdown
            label="Categories"
            options={TEAM_METADATA_CATEGORIES}
            values={categories}
            onChange={handleCategoriesChange}
            placeholder="Choose one or more categories"
            searchPlaceholder="Search categories…"
            helperText="Select every category that fits this club."
            disabled={submitting}
            multiple
          />

          <SearchableSelectDropdown
            label="Types"
            options={availableTypes}
            values={types}
            onChange={handleTypesChange}
            placeholder={availableTypeCategories.length > 0 ? 'Choose one or more types' : 'Select a category with supported types first'}
            searchPlaceholder="Search types…"
            emptyMessage="No matching types."
            helperText={availableTypeCategories.length > 0
              ? `Types available from: ${availableTypeCategories.join(', ')}`
              : 'Types appear when you choose a category that supports them.'}
            disabled={submitting || availableTypeCategories.length === 0}
            multiple
          />

          <SearchableSelectDropdown
            label="Primary Label"
            value={primaryLabel}
            onChange={setPrimaryLabel}
            options={primaryOptions}
            placeholder="Select the main category or type"
            searchPlaceholder="Search selected labels…"
            helperText="This is the main label shown elsewhere in the app."
            disabled={submitting || primaryOptions.length === 0}
          />

          <SelectDropdown
            label="Age Group"
            value={ageGroup}
            onChange={(e) => setAgeGroup(e.target.value)}
            options={ageGroups}
            disabled={submitting}
          />

          <fieldset className="age-restrictions" style={{ marginTop: '1.5rem' }}>
            <legend>Optional: Age Restrictions</legend>

            <div className="form-group">
              <label className="form-label">
                Min Age:
                <input
                  type="number"
                  value={ageMin}
                  onChange={(e) => setAgeMin(e.target.value)}
                  min="0"
                  disabled={submitting}
                />
              </label>
            </div>

            <div className="form-group">
              <label className="form-label">
                Max Age:
                <input
                  type="number"
                  value={ageMax}
                  onChange={(e) => setAgeMax(e.target.value)}
                  min="0"
                  disabled={submitting}
                />
              </label>
            </div>

            {(ageMin || ageMax) && (
              <>
                <p className="text-muted" style={{ marginTop: '0.5rem' }}>
                  Please select a reason for applying age restrictions.
                </p>
                <SelectDropdown
                  label="Justification"
                  value={justification}
                  onChange={(e) => setJustification(e.target.value)}
                  options={justificationOptions}
                  disabled={submitting}
                />
              </>
            )}
          </fieldset>

          <div className="form-actions">
            <SubmitButton
              type="button"
              label="Cancel"
              onClick={onClose}
              disabled={submitting}
              variant="ghost"
              size="sm"
            />
            <SubmitButton
              type="submit"
              label={submitting ? 'Saving…' : 'Save'}
              disabled={submitting}
              variant="primary"
            />
          </div>

        </form>
      </>
  );

}

EditMetadataForm.propTypes = {
  initialMetadata: PropTypes.object.isRequired,
  initialName: PropTypes.string,
  initialDescription: PropTypes.string,
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSave: PropTypes.func.isRequired,
};
